import React, { useMemo, useState, createElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, CheckCircle2Icon, TrendingUpIcon, ShieldCheckIcon, ZapIcon, ClockIcon, ArrowRightIcon, ArrowLeftIcon, AlertCircleIcon, InfoIcon, AwardIcon, FileTextIcon, DownloadIcon, PenToolIcon } from 'lucide-react';
import { BRAND } from '../../../shared/config/brand';
import { formatCurrencyAmount } from '../../../theme/tokens';
import { financingEligibility, getFinancingOffers, settlementOptions, SettlementSpeed } from '../mocks/financingData';
const formatMYR = (amount: number) => formatCurrencyAmount(amount, {
  currency: 'MYR'
});
export function FinancingApplyWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState<1 | 2 | 3 | 4 | 5 | 6>(1);
  // Step 3: Offers State
  const [borrowAmount, setBorrowAmount] = useState<number>(financingEligibility.maxAmount);
  const [selectedOfferId, setSelectedOfferId] = useState<string | null>(null);
  // Step 4: Payout State
  const [settlementSpeed, setSettlementSpeed] = useState<SettlementSpeed>('instant');
  const offers = useMemo(() => getFinancingOffers(borrowAmount), [borrowAmount]);
  const selectedOffer = offers.find(o => o.id === selectedOfferId);
  const selectedSettlement = settlementOptions.find(s => s.id === settlementSpeed);
  const handleNext = () => setStep(s => Math.min(s + 1, 6) as any);
  const handleBack = () => setStep(s => Math.max(s - 1, 1) as any);
  return <div className="fixed inset-0 z-50 bg-[#F8F9FA] overflow-y-auto flex flex-col" data-id="element-4788">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between sticky top-0 z-10 flex-shrink-0" data-id="element-4789">
        <button onClick={() => navigate('/bills/financing')} className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors" data-id="element-4790">
          <XIcon size={20} data-id="element-4791" />
        </button>
        <div className="flex items-center gap-3" data-id="element-4792">
          <span className="text-xs text-gray-500 font-medium" data-id="element-4793">
            Step {step} of 6
          </span>
          <div className="w-64 h-1.5 bg-gray-100 rounded-full overflow-hidden" data-id="element-4794">
            <div className="h-full bg-[var(--brand-primary)] transition-all duration-300 ease-in-out rounded-full" style={{
            width: `${step / 6 * 100}%`
          }} data-id="element-4795" />
          </div>
        </div>
        <div className="w-10" data-id="element-4796" />
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col" data-id="element-4797">
        <AnimatePresence mode="wait" data-id="element-4798">
          <motion.div key={step} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} transition={{
          duration: 0.3
        }} className="flex-1 max-w-4xl w-full mx-auto py-10 px-4 sm:px-6 flex flex-col" data-id="element-4799">
            {/* STEP 1: Eligibility Gates */}
            {step === 1 && <div className="flex-1 flex flex-col" data-id="element-4800">
                <div className="text-center mb-10" data-id="element-4801">
                  <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-4802">
                    <ShieldCheckIcon size={32} className="text-green-600" data-id="element-4803" />
                  </div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-3" data-id="element-4804">
                    You're eligible for instant financing
                  </h1>
                  <p className="text-gray-500 max-w-lg mx-auto" data-id="element-4805">
                    Your business meets all the requirements for {BRAND.name}{' '}
                    Financing. Here's a breakdown of your eligibility status.
                  </p>
                </div>

                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden mb-10" data-id="element-4806">
                  <div className="divide-y divide-gray-100" data-id="element-4807">
                    {financingEligibility.gates.map(gate => <div key={gate.id} className="p-6 flex items-center justify-between" data-id="element-4808">
                        <div className="flex items-center gap-4" data-id="element-4809">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${gate.passed ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-400'}`} data-id="element-4810">
                            <CheckCircle2Icon size={18} data-id="element-4811" />
                          </div>
                          <div data-id="element-4812">
                            <div className="font-semibold text-gray-900" data-id="element-4813">
                              {gate.label}
                            </div>
                            <div className="text-sm text-gray-500" data-id="element-4814">
                              Requirement: {gate.requirement}
                            </div>
                          </div>
                        </div>
                        <div className="text-right" data-id="element-4815">
                          <div className="text-sm font-medium text-gray-900" data-id="element-4816">
                            {gate.current}
                          </div>
                          <div className={`text-xs font-semibold mt-0.5 ${gate.passed ? 'text-green-600' : 'text-gray-500'}`} data-id="element-4817">
                            {gate.passed ? 'Passed' : 'Pending'}
                          </div>
                        </div>
                      </div>)}
                  </div>
                </div>

                <div className="mt-auto flex justify-center" data-id="element-4818">
                  <button onClick={handleNext} className="px-8 py-3 bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white rounded-xl font-semibold transition-colors shadow-sm flex items-center gap-2" data-id="element-4819">
                    View my analysis <ArrowRightIcon size={18} data-id="element-4820" />
                  </button>
                </div>
              </div>}

            {/* STEP 2: GMV Analysis & Signals */}
            {step === 2 && <div className="flex-1 flex flex-col" data-id="element-4821">
                <div className="text-center mb-10" data-id="element-4822">
                  <h1 className="text-3xl font-bold text-gray-900 mb-3" data-id="element-4823">
                    Your Business Analysis
                  </h1>
                  <p className="text-gray-500 max-w-lg mx-auto" data-id="element-4824">
                    We've analyzed your processing history to determine your
                    financing terms. Stronger signals mean better rates.
                  </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10" data-id="element-4825">
                  {/* Score Card */}
                  <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 flex flex-col items-center justify-center text-center" data-id="element-4826">
                    <div className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4" data-id="element-4827">
                      Underwriting Score
                    </div>
                    <div className="relative w-32 h-32 flex items-center justify-center mb-4" data-id="element-4828">
                      <svg className="w-full h-full transform -rotate-90" data-id="element-4829">
                        <circle cx="64" cy="64" r="56" stroke="currentColor" strokeWidth="8" fill="transparent" className="text-gray-100" data-id="element-4830" />
                        <motion.circle initial={{
                      strokeDasharray: '0 1000'
                    }} animate={{
                      strokeDasharray: `${financingEligibility.score / 100 * 351.8} 1000`
                    }} transition={{
                      duration: 1.5,
                      ease: 'easeOut'
                    }} cx="64" cy="64" r="56" stroke="currentColor" strokeWidth="8" fill="transparent" className="text-[var(--brand-primary)]" data-id="element-4831" />
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-4832">
                        <span className="text-3xl font-bold text-gray-900" data-id="element-4833">
                          {financingEligibility.score}
                        </span>
                        <span className="text-xs text-gray-500" data-id="element-4834">/ 100</span>
                      </div>
                    </div>
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-50 text-amber-700 rounded-full text-sm font-semibold" data-id="element-4835">
                      <AwardIcon size={14} data-id="element-4836" />
                      {financingEligibility.scoreTier} Tier
                    </div>
                  </div>

                  {/* GMV Chart & Signals */}
                  <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-200 shadow-sm p-6" data-id="element-4837">
                    <div className="flex items-center justify-between mb-6" data-id="element-4838">
                      <h3 className="font-bold text-gray-900" data-id="element-4839">
                        90-Day Volume Trend
                      </h3>
                      <span className="text-sm text-gray-500" data-id="element-4840">MYR</span>
                    </div>
                    <div className="h-40 flex items-end gap-1 mb-8" data-id="element-4841">
                      {financingEligibility.gmvSeries.map((val, i) => {
                    const max = Math.max(...financingEligibility.gmvSeries);
                    const height = `${val / max * 100}%`;
                    return <motion.div key={i} initial={{
                      height: 0
                    }} animate={{
                      height
                    }} transition={{
                      duration: 0.5,
                      delay: i * 0.01
                    }} className="flex-1 bg-[var(--brand-primary-light)] rounded-t-sm hover:bg-[var(--brand-primary)] transition-colors" data-id="element-4842" />;
                  })}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-4843">
                      {financingEligibility.signals.map(signal => <div key={signal.id} className="p-4 rounded-xl bg-gray-50 border border-gray-100" data-id="element-4844">
                          <div className="flex items-start justify-between mb-1" data-id="element-4845">
                            <div className="text-xs font-medium text-gray-500" data-id="element-4846">
                              {signal.label}
                            </div>
                            {signal.sentiment === 'positive' && <TrendingUpIcon size={14} className="text-green-500" data-id="element-4847" />}
                            {signal.sentiment === 'neutral' && <ArrowRightIcon size={14} className="text-gray-400" data-id="element-4848" />}
                            {signal.sentiment === 'caution' && <AlertCircleIcon size={14} className="text-amber-500" data-id="element-4849" />}
                          </div>
                          <div className="font-bold text-gray-900 mb-1" data-id="element-4850">
                            {signal.value}
                          </div>
                          <div className="text-xs text-gray-500" data-id="element-4851">
                            {signal.detail}
                          </div>
                        </div>)}
                    </div>
                  </div>
                </div>

                <div className="mt-auto flex justify-center gap-4" data-id="element-4852">
                  <button onClick={handleBack} className="px-8 py-3 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-xl font-semibold transition-colors shadow-sm" data-id="element-4853">
                    Back
                  </button>
                  <button onClick={handleNext} className="px-8 py-3 bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white rounded-xl font-semibold transition-colors shadow-sm flex items-center gap-2" data-id="element-4854">
                    See my offers <ArrowRightIcon size={18} data-id="element-4855" />
                  </button>
                </div>
              </div>}

            {/* STEP 3: Offers */}
            {step === 3 && <div className="flex-1 flex flex-col" data-id="element-4856">
                <div className="text-center mb-10" data-id="element-4857">
                  <h1 className="text-3xl font-bold text-gray-900 mb-3" data-id="element-4858">
                    Choose your financing
                  </h1>
                  <p className="text-gray-500 max-w-lg mx-auto" data-id="element-4859">
                    Select the amount you need and choose a repayment plan that
                    fits your cash flow.
                  </p>
                </div>

                {/* Amount Slider */}
                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 mb-8" data-id="element-4860">
                  <div className="flex justify-between items-end mb-6" data-id="element-4861">
                    <div data-id="element-4862">
                      <div className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-1" data-id="element-4863">
                        Borrow Amount
                      </div>
                      <div className="text-4xl font-bold text-gray-900" data-id="element-4864">
                        {formatMYR(borrowAmount)}
                      </div>
                    </div>
                    <div className="text-right" data-id="element-4865">
                      <div className="text-sm text-gray-500 mb-1" data-id="element-4866">
                        Max available
                      </div>
                      <div className="font-semibold text-gray-900" data-id="element-4867">
                        {formatMYR(financingEligibility.maxAmount)}
                      </div>
                    </div>
                  </div>
                  <input type="range" min={10000} max={financingEligibility.maxAmount} step={1000} value={borrowAmount} onChange={e => setBorrowAmount(Number(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[var(--brand-primary)]" data-id="element-4868" />
                </div>

                {/* Offers Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10" data-id="element-4869">
                  {offers.map(offer => <div key={offer.id} onClick={() => setSelectedOfferId(offer.id)} className={`relative p-6 rounded-2xl border-2 cursor-pointer transition-all ${selectedOfferId === offer.id ? 'border-[var(--brand-primary)] bg-white shadow-md' : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'}`} data-id="element-4870">
                      {offer.recommended ? <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs font-bold px-4 py-1 rounded-full whitespace-nowrap shadow-sm" data-id="element-4871">
                          {offer.highlight.toUpperCase()}
                        </div> : <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gray-100 text-gray-600 border border-gray-200 text-xs font-bold px-4 py-1 rounded-full whitespace-nowrap" data-id="element-4872">
                          {offer.highlight.toUpperCase()}
                        </div>}

                      <div className="text-center mb-6 mt-2" data-id="element-4873">
                        <h4 className="font-bold text-gray-900 text-xl" data-id="element-4874">
                          {offer.name}
                        </h4>
                        <p className="text-sm text-gray-500 mt-1" data-id="element-4875">
                          {offer.tagline}
                        </p>
                      </div>

                      <div className="space-y-4 mb-6" data-id="element-4876">
                        <div className="flex justify-between items-center text-sm" data-id="element-4877">
                          <span className="text-gray-500" data-id="element-4878">Term</span>
                          <span className="font-semibold text-gray-900" data-id="element-4879">
                            {offer.termMonths} months
                          </span>
                        </div>
                        <div className="flex justify-between items-center text-sm" data-id="element-4880">
                          <span className="text-gray-500" data-id="element-4881">Daily holdback</span>
                          <span className="font-semibold text-gray-900" data-id="element-4882">
                            {offer.holdbackRate}%
                          </span>
                        </div>
                        <div className="flex justify-between items-center text-sm" data-id="element-4883">
                          <span className="text-gray-500" data-id="element-4884">Total fees</span>
                          <span className="font-semibold text-gray-900" data-id="element-4885">
                            {formatMYR(offer.totalFees)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center text-sm" data-id="element-4886">
                          <span className="text-gray-500" data-id="element-4887">Est. daily</span>
                          <span className="font-semibold text-gray-900" data-id="element-4888">
                            ~{formatMYR(offer.estimatedDailyDeduction)}
                          </span>
                        </div>
                      </div>

                      <div className="pt-5 border-t border-gray-100 text-center" data-id="element-4889">
                        <div className="text-sm text-gray-500 mb-1" data-id="element-4890">
                          Total repayable
                        </div>
                        <div className="text-2xl font-bold text-[var(--brand-primary)]" data-id="element-4891">
                          {formatMYR(offer.totalRepayable)}
                        </div>
                      </div>

                      {selectedOfferId === offer.id && <div className="absolute top-4 right-4 text-[var(--brand-primary)]" data-id="element-4892">
                          <CheckCircle2Icon size={24} data-id="element-4893" />
                        </div>}
                    </div>)}
                </div>

                <div className="mt-auto flex justify-center gap-4" data-id="element-4894">
                  <button onClick={handleBack} className="px-8 py-3 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-xl font-semibold transition-colors shadow-sm" data-id="element-4895">
                    Back
                  </button>
                  <button onClick={handleNext} disabled={!selectedOfferId} className="px-8 py-3 bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white rounded-xl font-semibold transition-colors shadow-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-4896">
                    Continue <ArrowRightIcon size={18} data-id="element-4897" />
                  </button>
                </div>
              </div>}

            {/* STEP 4: Approval & Payout */}
            {step === 4 && selectedOffer && <div className="flex-1 flex flex-col max-w-2xl mx-auto w-full" data-id="element-4898">
                <div className="text-center mb-10" data-id="element-4899">
                  <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-4900">
                    <ZapIcon size={32} className="text-green-600" data-id="element-4901" />
                  </div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-3" data-id="element-4902">
                    Instant Approval
                  </h1>
                  <p className="text-gray-500" data-id="element-4903">
                    Your {formatMYR(borrowAmount)} financing is approved. Choose
                    how quickly you want the funds disbursed to your{' '}
                    {BRAND.name} settlement account.
                  </p>
                </div>

                <div className="space-y-4 mb-8" data-id="element-4904">
                  {settlementOptions.map(option => <label key={option.id} className={`flex items-start gap-4 p-6 rounded-2xl border-2 cursor-pointer transition-all ${settlementSpeed === option.id ? 'border-[var(--brand-primary)] bg-[var(--brand-primary-light)]' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-4905">
                      <input type="radio" name="settlementSpeed" value={option.id} checked={settlementSpeed === option.id} onChange={() => setSettlementSpeed(option.id as SettlementSpeed)} className="mt-1 w-5 h-5 text-[var(--brand-primary)] border-gray-300 focus:ring-[var(--brand-primary)]" data-id="element-4906" />
                      <div className="flex-1" data-id="element-4907">
                        <div className="flex justify-between items-start mb-1" data-id="element-4908">
                          <div className="font-bold text-gray-900 text-lg" data-id="element-4909">
                            {option.label}
                          </div>
                          <div className="font-semibold text-gray-900" data-id="element-4910">
                            {option.fee === 0 ? 'Free' : formatMYR(option.fee)}
                          </div>
                        </div>
                        <p className="text-sm text-gray-600 mb-3" data-id="element-4911">
                          {option.description}
                        </p>
                        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white rounded-md text-xs font-medium text-gray-600 border border-gray-200" data-id="element-4912">
                          <ClockIcon size={14} data-id="element-4913" />
                          ETA: {option.eta}
                        </div>
                      </div>
                    </label>)}
                </div>

                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 mb-10" data-id="element-4914">
                  <h3 className="font-bold text-gray-900 mb-4" data-id="element-4915">Summary</h3>
                  <div className="space-y-3 text-sm" data-id="element-4916">
                    <div className="flex justify-between" data-id="element-4917">
                      <span className="text-gray-500" data-id="element-4918">Financing amount</span>
                      <span className="font-medium text-gray-900" data-id="element-4919">
                        {formatMYR(borrowAmount)}
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-4920">
                      <span className="text-gray-500" data-id="element-4921">Plan</span>
                      <span className="font-medium text-gray-900" data-id="element-4922">
                        {selectedOffer.name} ({selectedOffer.termMonths} months)
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-4923">
                      <span className="text-gray-500" data-id="element-4924">Disbursement fee</span>
                      <span className="font-medium text-gray-900" data-id="element-4925">
                        {selectedSettlement?.fee === 0 ? 'Free' : formatMYR(selectedSettlement?.fee || 0)}
                      </span>
                    </div>
                    <div className="pt-3 border-t border-gray-100 flex justify-between font-bold text-lg" data-id="element-4926">
                      <span className="text-gray-900" data-id="element-4927">Net payout</span>
                      <span className="text-[var(--brand-primary)]" data-id="element-4928">
                        {formatMYR(borrowAmount - (selectedSettlement?.fee || 0))}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mt-auto flex justify-center gap-4" data-id="element-4929">
                  <button onClick={handleBack} className="px-8 py-3 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-xl font-semibold transition-colors shadow-sm" data-id="element-4930">
                    Back
                  </button>
                  <button onClick={handleNext} className="flex-1 px-8 py-3 bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white rounded-xl font-semibold transition-colors shadow-sm" data-id="element-4931">
                    Review loan agreement
                  </button>
                </div>
              </div>}

            {/* STEP 5: Loan Agreement & Terms */}
            {step === 5 && selectedOffer && <div className="flex-1 flex flex-col max-w-3xl mx-auto w-full" data-id="element-4932">
                <div className="text-center mb-8" data-id="element-4933">
                  <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-4934">
                    <FileTextIcon size={32} className="text-blue-600" data-id="element-4935" />
                  </div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-3" data-id="element-4936">
                    Loan Agreement & Terms
                  </h1>
                  <p className="text-gray-500" data-id="element-4937">
                    Please review the final terms of your financing facility
                    before signing.
                  </p>
                </div>

                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden mb-8" data-id="element-4938">
                  <div className="p-6 border-b border-gray-100" data-id="element-4939">
                    <h3 className="font-bold text-gray-900 mb-4" data-id="element-4940">
                      Facility Details
                    </h3>
                    <div className="grid grid-cols-2 gap-y-4 gap-x-8 text-sm" data-id="element-4941">
                      <div data-id="element-4942">
                        <div className="text-gray-500 mb-1" data-id="element-4943">
                          Principal Amount
                        </div>
                        <div className="font-semibold text-gray-900" data-id="element-4944">
                          {formatMYR(borrowAmount)}
                        </div>
                      </div>
                      <div data-id="element-4945">
                        <div className="text-gray-500 mb-1" data-id="element-4946">
                          Total Repayable
                        </div>
                        <div className="font-semibold text-[var(--brand-primary)]" data-id="element-4947">
                          {formatMYR(selectedOffer.totalRepayable)}
                        </div>
                      </div>
                      <div data-id="element-4948">
                        <div className="text-gray-500 mb-1" data-id="element-4949">Facility Term</div>
                        <div className="font-semibold text-gray-900" data-id="element-4950">
                          {selectedOffer.termMonths} months
                        </div>
                      </div>
                      <div data-id="element-4951">
                        <div className="text-gray-500 mb-1" data-id="element-4952">
                          Daily Holdback Rate
                        </div>
                        <div className="font-semibold text-gray-900" data-id="element-4953">
                          {selectedOffer.holdbackRate}% of daily settlement
                        </div>
                      </div>
                      <div data-id="element-4954">
                        <div className="text-gray-500 mb-1" data-id="element-4955">
                          Disbursement Account
                        </div>
                        <div className="font-semibold text-gray-900" data-id="element-4956">
                          {BRAND.name} Settlement Account
                        </div>
                      </div>
                      <div data-id="element-4957">
                        <div className="text-gray-500 mb-1" data-id="element-4958">
                          Disbursement Speed
                        </div>
                        <div className="font-semibold text-gray-900" data-id="element-4959">
                          {selectedSettlement?.label}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="p-6 border-b border-gray-100 bg-gray-50" data-id="element-4960">
                    <h3 className="font-bold text-gray-900 mb-4" data-id="element-4961">
                      Repayment Plan
                    </h3>
                    <p className="text-sm text-gray-600 mb-4" data-id="element-4962">
                      Repayment is fully automated. {BRAND.name} will deduct{' '}
                      {selectedOffer.holdbackRate}% from your daily card
                      processing settlements until the Total Repayable amount of{' '}
                      {formatMYR(selectedOffer.totalRepayable)} is fully
                      satisfied.
                    </p>
                    <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 p-3 rounded-lg border border-amber-100" data-id="element-4963">
                      <AlertCircleIcon size={16} className="flex-shrink-0" data-id="element-4964" />
                      <span data-id="element-4965">
                        If daily settlements are insufficient, the facility term
                        may be extended automatically without penalty.
                      </span>
                    </div>
                  </div>

                  <div className="p-6" data-id="element-4966">
                    <h3 className="font-bold text-gray-900 mb-4" data-id="element-4967">
                      Malaysia Business Financing Disclosure
                    </h3>
                    <div className="text-xs text-gray-500 space-y-3 h-32 overflow-y-auto pr-2 custom-scrollbar" data-id="element-4968">
                      <p data-id="element-4969">
                        This facility is provided by {BRAND.name} ("The
                        Financier") to the merchant ("The Customer") solely for
                        business and commercial purposes. This is not a consumer
                        credit facility and is not governed by the Consumer
                        Protection Act 1999.
                      </p>
                      <p data-id="element-4970">
                        By accepting these terms, the Customer irrevocably
                        authorizes the Financier to deduct the agreed holdback
                        percentage from daily settlement batches processed
                        through the Financier's acquiring network.
                      </p>
                      <p data-id="element-4971">
                        The Customer acknowledges that the total repayable
                        amount includes a fixed factor fee. There are no
                        compounding interest charges, late fees, or early
                        repayment penalties. The facility is considered in
                        default only if the Customer ceases processing with the
                        Financier or intentionally diverts processing volume to
                        evade repayment.
                      </p>
                      <p data-id="element-4972">
                        Governing Law: This agreement shall be governed by and
                        construed in accordance with the laws of Malaysia.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Signatures required */}
                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 mb-6" data-id="element-4973">
                  <div className="flex items-center justify-between mb-4" data-id="element-4974">
                    <h3 className="font-bold text-gray-900 flex items-center gap-2" data-id="element-4975">
                      <PenToolIcon size={18} className="text-[var(--brand-primary)]" data-id="element-4976" />
                      Signatures required
                    </h3>
                    <span className="text-xs font-semibold text-gray-600 bg-gray-100 px-2.5 py-1 rounded-full" data-id="element-4977">
                      2 of 2 required
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 mb-5" data-id="element-4978">
                    This facility requires 2 authorised signatures per your
                    mandate on file with {BRAND.name}.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-4979">
                    {[{
                  label: 'Authorised Signatory 1',
                  role: 'Director'
                }, {
                  label: 'Authorised Signatory 2',
                  role: 'Director / Company Secretary'
                }].map(s => <div key={s.label} className="p-4 rounded-xl border border-dashed border-gray-300 bg-gray-50" data-id="element-4980">
                        <div className="h-12 mb-3 border-b border-gray-300" data-id="element-4981" />
                        <div className="font-semibold text-gray-900 text-sm" data-id="element-4982">
                          {s.label}
                        </div>
                        <div className="flex items-center justify-between mt-0.5" data-id="element-4983">
                          <span className="text-xs text-gray-500" data-id="element-4984">
                            {s.role}
                          </span>
                          <span className="text-xs font-medium text-amber-600" data-id="element-4985">
                            Awaiting signature
                          </span>
                        </div>
                      </div>)}
                  </div>
                </div>

                {/* Download loan terms document */}
                <button onClick={() => {
              const blob = new Blob([`${BRAND.name} BUSINESS FINANCING — LOAN AGREEMENT\n\n` + `Lender: ${BRAND.name} Bank Berhad\n` + `Registered office: ${BRAND.address.oneLine}\n` + `SWIFT: ${BRAND.bank.swift}\n\n` + `Principal: ${formatMYR(borrowAmount)}\n` + `Plan: ${selectedOffer.name} (${selectedOffer.termMonths} months)\n` + `Daily holdback: ${selectedOffer.holdbackRate}%\n` + `Total repayable: ${formatMYR(selectedOffer.totalRepayable)}\n` + `Total fees: ${formatMYR(selectedOffer.totalFees)}\n\n` + `Authorised signatures required: 2\n\n` + `This facility is provided for business/commercial purposes only and is\n` + `governed by the laws of Malaysia and Bank Negara Malaysia guidelines.`], {
                type: 'text/plain'
              });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `${BRAND.shortCode}-Loan-Agreement.pdf`;
              a.click();
              URL.revokeObjectURL(url);
            }} className="w-full bg-white p-4 rounded-xl border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-colors flex items-center gap-4 text-left group mb-10" data-id="element-4986">
                  <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center flex-shrink-0 text-blue-600 group-hover:bg-blue-100 transition-colors" data-id="element-4987">
                    <DownloadIcon size={18} data-id="element-4988" />
                  </div>
                  <div data-id="element-4989">
                    <div className="font-semibold text-gray-900 text-sm" data-id="element-4990">
                      Download loan agreement (PDF)
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-4991">
                      Full loan terms &amp; Malaysia disclosure · 1.2 MB
                    </div>
                  </div>
                </button>

                <div className="mt-auto flex justify-center gap-4" data-id="element-4992">
                  <button onClick={handleBack} className="px-8 py-3 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-xl font-semibold transition-colors shadow-sm" data-id="element-4993">
                    Back
                  </button>
                  <button onClick={handleNext} className="flex-1 px-8 py-3 bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white rounded-xl font-semibold transition-colors shadow-sm flex items-center justify-center gap-2" data-id="element-4994">
                    <PenToolIcon size={18} data-id="element-4995" />
                    Sign & accept agreement
                  </button>
                </div>
              </div>}

            {/* STEP 6: Success */}
            {step === 6 && selectedOffer && <div className="flex-1 flex flex-col items-center justify-center max-w-lg mx-auto w-full text-center" data-id="element-4996">
                <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              type: 'spring',
              stiffness: 260,
              damping: 20,
              delay: 0.1
            }} className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mb-8" data-id="element-4997">
                  <CheckCircle2Icon size={48} className="text-green-600" data-id="element-4998" />
                </motion.div>
                <h1 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-4999">
                  Funds are on the way!
                </h1>
                <p className="text-lg text-gray-600 mb-8" data-id="element-5000">
                  Your {formatMYR(borrowAmount)} financing has been approved and
                  will be disbursed to your {BRAND.name} settlement account{' '}
                  <strong className="text-gray-900" data-id="element-5001">
                    {selectedSettlement?.eta.toLowerCase()}
                  </strong>
                  .
                </p>

                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 w-full mb-10 text-left" data-id="element-5002">
                  <div className="flex items-start gap-3 mb-4" data-id="element-5003">
                    <InfoIcon size={20} className="text-[var(--brand-primary)] flex-shrink-0 mt-0.5" data-id="element-5004" />
                    <div data-id="element-5005">
                      <h4 className="font-bold text-gray-900 mb-1" data-id="element-5006">
                        What happens next?
                      </h4>
                      <p className="text-sm text-gray-600" data-id="element-5007">
                        Repayment is fully automatic. Starting tomorrow, we will
                        deduct {selectedOffer.holdbackRate}% from your daily
                        settlements until the total repayable amount of{' '}
                        {formatMYR(selectedOffer.totalRepayable)} is reached.
                      </p>
                    </div>
                  </div>
                  <div className="pt-4 border-t border-gray-100 flex justify-between items-center text-sm" data-id="element-5008">
                    <span className="text-gray-500" data-id="element-5009">Facility Reference</span>
                    <span className="font-mono font-medium text-gray-900" data-id="element-5010">
                      CIMB-SME-{Math.floor(10000 + Math.random() * 90000)}
                    </span>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 w-full" data-id="element-5011">
                  <button onClick={() => navigate('/bills/financing')} className="flex-1 px-8 py-3 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-xl font-semibold transition-colors shadow-sm" data-id="element-5012">
                    Back to Financing
                  </button>
                  <button onClick={() => navigate('/bills/financing')} className="flex-1 px-8 py-3 bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white rounded-xl font-semibold transition-colors shadow-sm" data-id="element-5013">
                    View Facility
                  </button>
                </div>
              </div>}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>;
}